import api from "./api";

/** One row in the GET /quiz-schedules list response. */
export interface QuizScheduleSummary {
  id: string;
  name: string;
  scope: "platform" | "clubs";
  quizzes_per_week: number;
  start_date: string;
  end_date: string;
  num_weeks: number;
  is_active: boolean;
  picked_quiz_count: number;
  drop_count: number;
  target_club_count: number;
  created_by: { id: string; email: string; full_name: string | null };
  created_at: string;
}

export interface QuizScheduleDetail {
  id: string;
  name: string;
  scope: "platform" | "clubs";
  quizzes_per_week: number;
  start_date: string;
  num_weeks: number;
  is_active: boolean;
  created_by: { id: string; email: string; full_name: string | null };
  created_at: string;
  updated_at: string;
  target_clubs: Array<{ id: string; name: string }>;
  picked_quizzes: Array<{ id: string; title: string; topic: string }>;
  drops: Array<{
    id: string;
    week_index: number;
    week_start: string;
    quiz: {
      id: string;
      title: string;
      topic: string;
      difficulty: string;
      reward_points: number;
    };
  }>;
}

export interface CreateSchedulePayload {
  name: string;
  scope: "platform" | "clubs";
  target_club_ids?: string[];
  quiz_ids: string[];
  quizzes_per_week: number;
  start_date: string;
  num_weeks: number;
  // Optional manual layout from the wizard's drag-and-drop preview.
  // When omitted the backend round-robins through quiz_ids.
  drops?: Array<{ week_index: number; quiz_id: string }>;
}

export async function listQuizSchedules(): Promise<QuizScheduleSummary[]> {
  const { data } = await api.get<QuizScheduleSummary[]>("/quiz-schedules");
  return data;
}

export async function getQuizSchedule(id: string): Promise<QuizScheduleDetail> {
  const { data } = await api.get<QuizScheduleDetail>(`/quiz-schedules/${id}`);
  return data;
}

export async function createQuizSchedule(payload: CreateSchedulePayload): Promise<{ id: string }> {
  const { data } = await api.post<{ id: string }>("/quiz-schedules", payload);
  return data;
}

export async function patchQuizSchedule(
  id: string,
  payload: Partial<{ is_active: boolean; name: string }>
) {
  const { data } = await api.patch(`/quiz-schedules/${id}`, payload);
  return data;
}

export async function deleteQuizSchedule(id: string): Promise<{ success: true }> {
  const { data } = await api.delete<{ success: true }>(`/quiz-schedules/${id}`);
  return data;
}

/**
 * Athlete-facing — quizzes scheduled to drop this week, deduplicated
 * across all schedules the caller is visible in (platform-scope +
 * any club-scope schedule targeting the athlete's club).
 */
export interface AthleteScheduledQuiz {
  drop_id: string;
  week_start: string;
  schedule: { id: string; name: string; scope: "platform" | "clubs" };
  quiz: {
    id: string;
    title: string;
    description: string | null;
    topic: string;
    difficulty: string;
    category?: string;
    reward_points: number;
    max_points?: number;
    video_url: string | null;
    video_mode: string | null;
    theme_id: string | null;
  };
  // Per-quiz interaction state for the current athlete. Populated
  // server-side so the mobile app can render a badge without a
  // second roundtrip per card.
  status?: "not_started" | "in_progress" | "completed";
  attempt_count?: number;
  completed_count?: number;
  best_score?: number | null; // percentage 0-100, null if no completed attempt
  is_perfect?: boolean;
}

export async function getAthleteThisWeekDrops(): Promise<{
  week_start: string;
  next_week_start: string;
  drops: AthleteScheduledQuiz[];
}> {
  const { data } = await api.get(`/quiz-schedules/athlete/this-week`);
  return data;
}

/**
 * Club-admin facing — weekly schedule of quizzes visible to the club
 * with per-quiz metrics scoped to THAT club's athletes only.
 */
export interface ClubWeekDrop {
  drop_id: string;
  quiz: {
    id: string;
    title: string;
    topic: string;
    difficulty: string;
    reward_points: number;
    description: string | null;
  };
  schedule: { id: string; name: string; scope: "platform" | "clubs" };
  stats: {
    views: number;
    started: number;
    completed: number;
    avg_score: number;
  };
}

export interface ClubWeeksResponse {
  club_id: string;
  total_athletes: number;
  current_week_start: string;
  weeks: Array<{
    week_start: string;
    week_index_relative: number;
    drops: ClubWeekDrop[];
  }>;
}

export async function getClubScheduleWeeks(
  clubId: string,
  opts?: { weeks_back?: number; weeks_ahead?: number }
): Promise<ClubWeeksResponse> {
  const params = new URLSearchParams();
  if (opts?.weeks_back != null) params.set("weeks_back", String(opts.weeks_back));
  if (opts?.weeks_ahead != null) params.set("weeks_ahead", String(opts.weeks_ahead));
  const qs = params.toString();
  const { data } = await api.get(
    `/quiz-schedules/club/${clubId}/weeks${qs ? "?" + qs : ""}`
  );
  return data;
}
